import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, BadgeCheckIcon, PhoneIcon, Globe2Icon } from 'lucide-react';
interface HeroProps {
  onCtaClick: () => void;
}
export function Hero({
  onCtaClick
}: HeroProps) {
  return <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-brand-navy via-[#0d2a52] to-brand-deep" data-id="element-113">
      {/* Enhanced decorative elements with gold accents */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-114">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl" data-id="element-115" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-aqua/20 rounded-full blur-3xl" data-id="element-116" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-gold/5 rounded-full blur-3xl" data-id="element-117" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 min-h-screen flex flex-col" data-id="element-118">
        {/* Premium Header Navigation */}
        <motion.nav initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex items-center justify-between py-6 mb-8 gap-4" data-id="element-119">
          {/* Medical Practice Header - Text Only */}
          <motion.div className="flex flex-col gap-1 flex-shrink-0" whileHover={{
          scale: 1.02
        }} transition={{
          duration: 0.2
        }} data-id="element-120">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-none" data-id="element-121">
              Dr. Mark Joy, MD
            </h2>
            <p className="text-xs sm:text-sm text-white/70 font-light" data-id="element-122">
              Licensed Medical Doctor – New York
            </p>
          </motion.div>

          {/* Premium CTA Button - Mobile Optimized */}
          <motion.button onClick={onCtaClick} whileTap={{
          scale: 0.95
        }} className="relative overflow-hidden px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-lime-400 to-lime-500 rounded-full shadow-lg shadow-lime-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-lime-400/70 active:shadow-lime-500/80 flex-shrink-0" data-id="element-123">
            <span className="relative z-10 flex items-center gap-1.5 sm:gap-2 text-gray-900 font-bold text-xs sm:text-sm md:text-base whitespace-nowrap" data-id="element-124">
              <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-125" />
              <span className="hidden sm:inline" data-id="element-126">Request Consultation</span>
              <span className="sm:hidden" data-id="element-127">Request Consultation</span>
            </span>
          </motion.button>
        </motion.nav>

        {/* Main content grid - 2 columns on desktop */}
        <div className="flex-1 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-128">
          {/* Left column - Text content */}
          <div data-id="element-129">
            {/* Main headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.15] mb-6" data-id="element-130">
              Professional Medical Consultations{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary" data-id="element-131">
                by Appointment
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-base sm:text-lg md:text-xl text-white/90 mb-4 leading-relaxed" data-id="element-132">
              Request a consultation with Dr. Mark Joy, MD. Appointments are
              coordinated by our medical team.
            </motion.p>

            {/* Support line */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="text-sm sm:text-base text-white/90 mb-8 sm:mb-10 flex items-center gap-2 font-medium" data-id="element-133">
              <Globe2Icon className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-134" />
              <span data-id="element-135">
                All consultations are scheduled in advance and confirmed by a
                coordinator.
              </span>
            </motion.p>

            {/* CTA Button - Mobile Optimized & Elegant */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="mb-10 sm:mb-12" data-id="element-136">
              <motion.button onClick={onCtaClick} whileTap={{
              scale: 0.98
            }} className="group relative w-full sm:w-auto overflow-hidden px-6 py-3.5 sm:px-8 sm:py-4 md:px-12 md:py-5 bg-gradient-to-r from-lime-400 to-lime-500 rounded-full shadow-2xl shadow-lime-400/60 transition-all duration-300 hover:shadow-2xl hover:shadow-lime-400/80" aria-label="Request an appointment" data-id="element-137">
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3 text-gray-900 font-bold text-sm sm:text-base md:text-lg" data-id="element-138">
                  <span className="whitespace-nowrap" data-id="element-139">
                    Request an Appointment
                  </span>
                  <span className="text-base sm:text-lg md:text-xl transition-transform duration-300 group-hover:translate-x-1" data-id="element-140">
                    →
                  </span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-lime-500 to-lime-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-141" />
              </motion.button>

              {/* Micro-text under button */}
              <p className="text-center text-xs sm:text-sm text-white/60 mt-3" data-id="element-142">
                Confidential • Professional • Appointment-based care
              </p>
            </motion.div>

            {/* Premium Trust badges - Minimal & Elegant */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="flex flex-wrap gap-2 sm:gap-3 md:gap-4" data-id="element-143">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-144">
                <ShieldCheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-brand-gold flex-shrink-0" data-id="element-145" />
                <span className="text-[10px] sm:text-xs text-white/90 font-light whitespace-nowrap" data-id="element-146">
                  Licensed Medical Doctor
                </span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-147">
                <BadgeCheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-brand-gold flex-shrink-0" data-id="element-148" />
                <span className="text-[10px] sm:text-xs text-white/90 font-light whitespace-nowrap" data-id="element-149">
                  New York Licensed
                </span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10" data-id="element-150">
                <Globe2Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-brand-gold flex-shrink-0" data-id="element-151" />
                <span className="text-[10px] sm:text-xs text-white/90 font-light whitespace-nowrap" data-id="element-152">
                  English & Spanish
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right column - Clinic image with luxury styling */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="relative" data-id="element-153">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-brand-gold/20" data-id="element-154">
              <img src="https://cdn.magicpatterns.com/uploads/oZhPEUrTakisxHD9MECTt2/pasted-image.png" alt="Professional medical consultation environment" className="w-full h-auto object-cover" data-id="element-155" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" data-id="element-156" />

              {/* Elegant badge overlay */}
              <div className="absolute top-6 left-6 px-5 py-2 bg-brand-gold/95 backdrop-blur-sm rounded-full shadow-xl" data-id="element-157">
                <p className="text-brand-navy font-semibold text-sm tracking-wide" data-id="element-158">
                  Professional Environment
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}